import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Ship, ArrowRight, CheckCircle2, Clock, Globe, Shield, 
  Package, Zap, Phone, Mail, Anchor, Users, X, Check, Box
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

const OceanFreightPage = () => {
  const { t } = useTranslation();

  // Structured data for Ocean Freight service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Ocean Freight Coordination",
    "description": "FCL and LCL ocean freight coordination through trusted shipping partners.",
    "provider": {
      "@type": "Organization",
      "name": "Elementary Cargo B.V.",
      "url": "https://elementarycargo.com"
    },
    "serviceType": "Ocean Freight",
    "areaServed": "Worldwide"
  };

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Ocean Freight", path: "/services/ocean-freight" }
  ];

  const features = [
    { 
      icon: Users, 
      title: t('oceanFreightPage.features.singleContact'), 
      description: t('oceanFreightPage.features.singleContactDesc')
    },
    { 
      icon: Globe, 
      title: t('oceanFreightPage.features.carrierNetwork'), 
      description: t('oceanFreightPage.features.carrierNetworkDesc')
    },
    { 
      icon: Shield, 
      title: t('oceanFreightPage.features.documented'), 
      description: t('oceanFreightPage.features.documentedDesc')
    },
    { 
      icon: Clock, 
      title: t('oceanFreightPage.features.sailingUpdates'), 
      description: t('oceanFreightPage.features.sailingUpdatesDesc')
    }
  ];

  const services = [
    {
      title: t('oceanFreightPage.services.fcl'),
      description: t('oceanFreightPage.services.fclDesc'),
      features: t('oceanFreightPage.services.fclFeatures', { returnObjects: true })
    },
    {
      title: t('oceanFreightPage.services.lcl'),
      description: t('oceanFreightPage.services.lclDesc'),
      features: t('oceanFreightPage.services.lclFeatures', { returnObjects: true })
    },
    {
      title: t('oceanFreightPage.services.port'),
      description: t('oceanFreightPage.services.portDesc'),
      features: t('oceanFreightPage.services.portFeatures', { returnObjects: true })
    },
    {
      title: t('oceanFreightPage.services.project'),
      description: t('oceanFreightPage.services.projectDesc'),
      features: t('oceanFreightPage.services.projectFeatures', { returnObjects: true })
    }
  ];

  const tradeRoutes = t('oceanFreightPage.tradeRoutesList', { returnObjects: true });
  const whatWeDo = t('oceanFreightPage.whatWeDoList', { returnObjects: true });
  const whatWeDontDo = t('oceanFreightPage.whatWeDoNotList', { returnObjects: true });

  const containers = [
    {
      type: t('oceanFreightPage.containers.20ft'),
      description: t('oceanFreightPage.containers.20ftDesc'),
      capacity: t('oceanFreightPage.containers.20ftCapacity'),
      icon: Box
    },
    {
      type: t('oceanFreightPage.containers.40ft'),
      description: t('oceanFreightPage.containers.40ftDesc'),
      capacity: t('oceanFreightPage.containers.40ftCapacity'),
      icon: Box
    },
    {
      type: t('oceanFreightPage.containers.40hc'),
      description: t('oceanFreightPage.containers.40hcDesc'),
      capacity: t('oceanFreightPage.containers.40hcCapacity'),
      icon: Box
    }
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="ocean-freight-page">
      <SEO 
        titleKey="seo.oceanFreight.title"
        descriptionKey="seo.oceanFreight.description"
        path="/services/ocean-freight"
        structuredData={serviceSchema}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1920&q=80"
            alt="Container ship - coordinated through our shipping partners"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gold/20 rounded-xl flex items-center justify-center">
                <Ship className="w-7 h-7 text-gold" />
              </div>
              <span className="text-gold text-sm font-semibold tracking-wider uppercase">{t('oceanFreightPage.badge')}</span>
            </div>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('oceanFreightPage.heroTitle')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed mb-8">
              {t('oceanFreightPage.heroSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/quote" data-testid="ocean-freight-quote-btn">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all">
                  {t('oceanFreightPage.requestQuote')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg">
                  {t('oceanFreightPage.talkWithUs')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('oceanFreightPage.whyCoordinateWithUs')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900">
              {t('oceanFreightPage.structuredCoordination')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-navy-900/5 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-navy-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('oceanFreightPage.whatWeCoordinate')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('oceanFreightPage.oceanFreightOptions')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('oceanFreightPage.oceanFreightOptionsDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 transition-colors">
                  <CardContent className="p-8">
                    <h3 className="font-space text-xl font-bold text-navy-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {Array.isArray(service.features) && service.features.map((feature) => (
                        <li key={`${service.title.slice(0, 10)}-${feature.slice(0, 15)}`} className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trade Routes */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('oceanFreightPage.globalCoverage')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-6">
                {t('oceanFreightPage.tradeRoutes')}
              </h2>
              <p className="text-platinum-light mb-8">
                {t('oceanFreightPage.tradeRoutesDesc')}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Array.isArray(tradeRoutes) && tradeRoutes.map((route, index) => (
                  <div key={`item-${index}`} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                    <span className="text-sm">{route}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1605539362999-7f0c3e6e6e2c?w=800&q=80"
                  alt="Global trade routes coordinated through shipping partners"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Container Types */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('oceanFreightPage.equipment')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900">
              {t('oceanFreightPage.containerTypes')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {containers.map((container, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-offwhite border border-platinum-light hover:border-gold/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-navy-900/5 flex items-center justify-center mx-auto mb-4">
                      <container.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-navy-900 mb-2">{container.type}</h3>
                    <p className="text-slate-600 text-sm mb-2">{container.description}</p>
                    <p className="text-gold font-semibold text-sm">{container.capacity}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('oceanFreightPage.ourRole')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('oceanFreightPage.clearAboutWhatWeDo')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('oceanFreightPage.transparencyDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-green-200 bg-green-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('oceanFreightPage.whatWeDo')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDo) && whatWeDo.map((item, index) => (
                      <li key={`item-${index}`} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center">
                      <X className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('oceanFreightPage.whatWeDoNot')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDontDo) && whatWeDontDo.map((item, index) => (
                      <li key={`item-${index}`} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-4">
              {t('oceanFreightPage.needCoordination')}
            </h2>
            <p className="text-platinum-light text-lg mb-8">
              {t('oceanFreightPage.needCoordinationDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all w-full sm:w-auto">
                  {t('oceanFreightPage.requestAQuote')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 text-platinum-light text-sm">
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-4 h-4" /> info@elementarycargo.com
              </a>
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-4 h-4" /> +31 62 538 1824
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OceanFreightPage;
